import React, { useState, useCallback } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Alert, Spinner } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { actions } from 'modules/user';
import getContent from 'lib/getContent';
import { useWLC } from '../../contexts/WLCContext';
import { useTrack } from '../../contexts/TrackContext';
import './styles.scss';

/**
 * DebugProgressPopup – Developer utility for testing WLC progress flows
 *
 * Allows developers to:
 * - Advance to the next week in current track
 * - Advance to the next track in priority order
 * - Reset all WLC progress and onboarding
 */
const DebugProgressPopup = () => {
  const { wlcProgress, updateWlcProgress, productSlug, authToken, loadingState } = useWLC();
  const ONBOARDING_ID = process.env.ONBOARDING_ID;
  const dispatch = useDispatch();
  const { weeks } = useTrack();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info'); // 'info', 'success', 'danger'

  const toggle = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  const showMessage = useCallback((text, type = 'info') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => setMessage(''), 3000);
  }, []);

  /**
   * Advance to next week in current track
   */
  const handleNextWeek = useCallback(async () => {
    try {
      setIsLoading(true);

      if (!wlcProgress?.currentTrack) {
        showMessage('No current track found', 'danger');
        return;
      }

      if (!weeks || weeks.length === 0) {
        showMessage('No weeks found in track', 'danger');
        return;
      }

      const currentWeekIndex = weeks.findIndex(w => w.slug === wlcProgress.currentWeekSlug);

      if (currentWeekIndex === -1) {
        showMessage('Current week not found in track', 'danger');
        return;
      }

      if (currentWeekIndex >= weeks.length - 1) {
        showMessage('Already on the last week of track', 'danger');
        return;
      }

      const nextWeek = weeks[currentWeekIndex + 1];

      updateWlcProgress({
        currentWeekSlug: nextWeek.slug
      });

      showMessage(`Advanced to week: ${nextWeek.name || nextWeek.slug}`, 'success');
      setIsLoading(false);
    } catch (err) {
      console.error('Error advancing to next week:', err);
      showMessage(`Error: ${err.message}`, 'danger');
      setIsLoading(false);
    }
  }, [wlcProgress, weeks, updateWlcProgress, showMessage]);

  /**
   * Advance to next track in priority order
   */
  const handleNextTrack = useCallback(async () => {
    try {
      setIsLoading(true);

      const priorityOrder = wlcProgress?.trackPriorityOrder || [];

      if (priorityOrder.length === 0) {
        showMessage('No tracks in priority order', 'danger');
        setIsLoading(false);
        return;
      }

      const currentTrackIndex = priorityOrder.indexOf(wlcProgress?.currentTrack);
      const nextTrackSlug = priorityOrder[currentTrackIndex >= 0 ? currentTrackIndex + 1 : 0];

      // Fetch the full track data with children using getContent
      const nextTrack = await getContent(nextTrackSlug, productSlug, authToken, '?depth=2');

      if (!nextTrack) {
        showMessage(`Track ${nextTrackSlug} not found`, 'danger');
        setIsLoading(false);
        return;
      }

      // Get the first week from the next track
      const nextWeeks = nextTrack.childrenIndexes?.week || [];

      if (nextWeeks.length === 0) {
        showMessage('Next track has no weeks', 'danger');
        setIsLoading(false);
        return;
      }

      const firstWeekIndex = nextWeeks[0];
      const firstWeek = nextTrack.Children?.[firstWeekIndex];

      if (!firstWeek) {
        showMessage('Could not find first week of next track', 'danger');
        setIsLoading(false);
        return;
      }

      updateWlcProgress({
        currentTrack: nextTrackSlug,
        currentWeekSlug: firstWeek.slug
      });

      showMessage(`Moved to track: ${nextTrack.name || nextTrackSlug}`, 'success');
      setIsLoading(false);
    } catch (err) {
      console.error('Error advancing to next track:', err);
      showMessage(`Error: ${err.message}`, 'danger');
      setIsLoading(false);
    }
  }, [wlcProgress, productSlug, authToken, updateWlcProgress, showMessage]);

  /**
   * Reset all WLC progress and onboarding
   */
  const handleReset = useCallback(async () => {
    try {
      setIsLoading(true);

      // Reset wlcProgress
      dispatch(actions.postUserData(1, {}));
      dispatch(actions.postUserData(ONBOARDING_ID, {}));

      showMessage('WLC Progress and Onboarding Reset', 'success');
      setIsLoading(false);

      // Reload page after brief delay to reflect changes
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (err) {
      console.error('Error resetting WLC progress:', err);
      showMessage(`Error: ${err.message}`, 'danger');
      setIsLoading(false);
    }
  }, [updateWlcProgress, showMessage]);

  // Only show in development
  /*if (process.env.NODE_ENV !== 'development') {
    return null;
  }*/

  return (
    <div className="debug-progress-popup-container">
      {/* Floating trigger button */}
      <Button size="sm" color="warning" onClick={toggle} className="debug-trigger-button" title="Debug WLC Progress">
        🐛
      </Button>

      {/* Modal */}
      <Modal isOpen={isOpen} toggle={toggle} centered>
        <ModalHeader toggle={toggle}>Debug WLC Progress</ModalHeader>
        <ModalBody>
          {message && (
            <Alert color={messageType} className="mb-3">
              {message}
            </Alert>
          )}

          <div className="debug-info mb-4">
            <h5>Current Progress</h5>
            <ul className="list-unstyled">
              <li>
                <strong>Track:</strong> {wlcProgress?.currentTrack || 'None'}
              </li>
              <li>
                <strong>Week:</strong> {wlcProgress?.currentWeekSlug || 'None'}
              </li>
              <li>
                <strong>Priority Order:</strong> {wlcProgress?.trackPriorityOrder?.join(', ') || 'None'}
              </li>
            </ul>
          </div>

          <div className="debug-actions">
            <h5>Actions</h5>
            <p className="text-muted small mb-3">
              Use these buttons to test progress flows. Page will reload after reset.
            </p>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={handleNextWeek}
            disabled={isLoading || !weeks || weeks.length === 0}
            className="me-2"
          >
            {isLoading ? <Spinner size="sm" /> : null}
            Next Week
          </Button>
          <Button
            color="info"
            onClick={handleNextTrack}
            disabled={isLoading || !wlcProgress?.trackPriorityOrder?.length}
            className="me-2"
          >
            {isLoading ? <Spinner size="sm" /> : null}
            Next Track
          </Button>
          <Button color="danger" onClick={handleReset} disabled={isLoading} className="me-2">
            {isLoading ? <Spinner size="sm" /> : null}
            Reset All
          </Button>
          <Button color="secondary" onClick={toggle}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default DebugProgressPopup;
